/**
 * The thing in your hand, as an object in the world.
 *
 * Until now the game told you what you were holding in a slot at the bottom
 * of the screen and nowhere else. You could not see it, other players could
 * not see it, and swinging produced no movement at all - so a blow landed
 * with nothing on screen to say it had.
 *
 * Two shapes, because one would be wrong for half the things:
 *
 *   - **A block is a cube,** textured from the same tiles the world is. A
 *     cобака of соснових дощок held as a flat card would read as a picture of
 *     planks rather than as planks.
 *   - **Everything else is a flat sprite,** the icon standing up in the air.
 *     A шабля is drawn as a shape on a tile and a cube of it would be a
 *     block with a sword painted on all six sides.
 *
 * The texture is the 2D atlas the interface already draws its icons from,
 * not the array texture the terrain uses: the array exists so that greedy
 * meshing can repeat a tile across eight blocks, which is a problem a single
 * quad does not have.
 */

import * as THREE from "three";
import { buildHeldCanvas } from "./atlas.js";
import { ATLAS_TILES, TILE_PX } from "../core/block/tiles.js";
import { paintTile, TILES, shafted } from "../core/block/tilePixels.js";
import { extrudeSprite, gripOf } from "../core/block/spriteMesh.js";
import { itemTile, FIRST_NON_BLOCK_ITEM } from "../core/block/items.js";
import { faceTile, isStairsRender, isCrossRender } from "../core/block/registry.js";
import { heldModel } from "./heldModels.js";
import type { Tone } from "../core/text/tone.js";

/**
 * How thick a drawn item stands, as a fraction of its own width.
 *
 * Two of its sixteen pixels rather than one. One is honest and invisible: at
 * the angle a thing is held the rim is a hairline and the object still reads
 * as paper. Two is the smallest that says "solid" from across the screen.
 */
const ITEM_THICKNESS = 2 / 16;

/** One texture for every held thing there will ever be. */
let sheet: THREE.CanvasTexture | null = null;

function atlasTexture(): THREE.CanvasTexture {
  if (sheet !== null) return sheet;
  const texture = new THREE.CanvasTexture(buildHeldCanvas());
  // Nearest, or a sixteen-pixel tile blurs into its neighbours the moment it
  // is drawn at any size but its own.
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  texture.colorSpace = THREE.SRGBColorSpace;
  sheet = texture;
  return texture;
}

/** Where one tile sits in the sheet, in texture coordinates. */
function tileRect(tile: number): { u: number; v: number; s: number } {
  const s = 1 / ATLAS_TILES;
  const col = tile % ATLAS_TILES;
  // The canvas is drawn top-down and texture coordinates run bottom-up.
  const row = ATLAS_TILES - 1 - Math.floor(tile / ATLAS_TILES);
  return { u: col * s, v: row * s, s };
}

/**
 * Squeeze a geometry's unit UVs into one tile.
 *
 * @param faces which run of four vertices gets which tile, in the order
 *   three.js builds a box: +x, -x, +y, -y, +z, -z.
 */
function mapUvs(geometry: THREE.BufferGeometry, faces: readonly number[]): void {
  const uv = geometry.getAttribute("uv") as THREE.BufferAttribute;
  for (let f = 0; f < faces.length; f++) {
    const { u, v, s } = tileRect(faces[f]!);
    for (let i = f * 4; i < f * 4 + 4; i++) {
      uv.setXY(i, u + uv.getX(i) * s, v + uv.getY(i) * s);
    }
  }
  uv.needsUpdate = true;
}

/**
 * A mesh for whatever `id` is, or null for an empty hand.
 *
 * One unit across, so whoever holds it decides how big it looks - the fist
 * in front of the camera and the fist on a body forty blocks away want very
 * different numbers and neither belongs in here.
 */
export function makeHeldMesh(id: number, tone: Tone = "family"): THREE.Object3D | null {
  if (id === 0) return null;
  // A modelled thing, where there is one and it has arrived. See heldModels.
  const modelled = heldModel(id);
  if (modelled !== null) return modelled;

  const material = new THREE.MeshBasicMaterial({
    map: atlasTexture(),
    // A cut-out, not a blend: pixel art wants a hard edge, and alphaTest
    // keeps the depth buffer honest where `transparent` would not - a leaf
    // block held in the hand would otherwise sort against the world wrongly.
    alphaTest: 0.5,
    // Seen from both sides as the arm turns, and through its own holes.
    side: THREE.DoubleSide,
  });

  // A plant is a picture, like a sabre, and not a box with a beet on every
  // side. Everything else that is a block is its cube.
  if (id < FIRST_NON_BLOCK_ITEM && !isCrossRender(id)) {
    const tiles = [
      faceTile(id, 0, true), faceTile(id, 0, false),
      faceTile(id, 1, true), faceTile(id, 1, false),
      faceTile(id, 2, true), faceTile(id, 2, false),
    ];
    /*
     * A stair in the hand is a stair, not a cube of its material.
     *
     * The world draws it as two boxes - the back half full height, the
     * front half a step - and the hand does the same, so that what you are
     * holding is visibly what you are about to put down. "Поміняй їх
     * картинку ... в руці, щоб було видно, що то сходи."
     */
    if (isStairsRender(id)) {
      const back = new THREE.BoxGeometry(1, 1, 0.5);
      back.translate(0, 0, -0.25);
      mapUvs(back, tiles);
      const step = new THREE.BoxGeometry(1, 0.5, 0.5);
      step.translate(0, -0.25, 0.25);
      mapUvs(step, tiles);
      return new THREE.Mesh(joined(back, step), material);
    }
    const box = new THREE.BoxGeometry(1, 1, 1);
    mapUvs(box, tiles);
    return new THREE.Mesh(box, material);
  }

  const solid = new THREE.Mesh(spriteGeometry(itemTile(id, tone)), material);
  // Its shape is shared with every other copy of this item; only the material
  // belongs to this one. `disposeHeldMesh` reads this.
  solid.userData.sharedGeometry = true;
  return solid;
}

/**
 * Two boxes as one geometry.
 *
 * Position, normal and uv laid end to end, the second box's indices moved
 * up by the first's vertex count. Small enough to write here rather than
 * pull in the examples' merge helper for two boxes.
 */
function joined(a: THREE.BufferGeometry, b: THREE.BufferGeometry): THREE.BufferGeometry {
  const out = new THREE.BufferGeometry();
  for (const name of ["position", "normal", "uv"] as const) {
    const pa = a.getAttribute(name) as THREE.BufferAttribute;
    const pb = b.getAttribute(name) as THREE.BufferAttribute;
    const merged = new Float32Array(pa.array.length + pb.array.length);
    merged.set(pa.array as Float32Array, 0);
    merged.set(pb.array as Float32Array, pa.array.length);
    out.setAttribute(name, new THREE.BufferAttribute(merged, pa.itemSize));
  }
  const ia = a.getIndex()!;
  const ib = b.getIndex()!;
  const shift = (a.getAttribute("position") as THREE.BufferAttribute).count;
  const index = new Uint16Array(ia.count + ib.count);
  for (let i = 0; i < ia.count; i++) index[i] = ia.getX(i);
  for (let i = 0; i < ib.count; i++) index[ia.count + i] = ib.getX(i) + shift;
  out.setIndex(new THREE.BufferAttribute(index, 1));
  a.dispose();
  b.dispose();
  return out;
}

/**
 * The solid form of one tile, built once and kept.
 *
 * A шабля is the same shape every time somebody picks one up, and rebuilding
 * a hundred faces each time the belt changes would be work done for nothing.
 * Geometry is shared between meshes, so `disposeHeldMesh` leaves it alone.
 */
const shapes = new Map<number, THREE.BufferGeometry>();

function spriteGeometry(tile: number): THREE.BufferGeometry {
  const found = shapes.get(tile);
  if (found !== undefined) return found;

  const spec = TILES[tile];
  const pixels = spec === undefined
    ? new Uint8Array(TILE_PX * TILE_PX * 4)
    : paintTile(tile, spec);
  const mesh = extrudeSprite(pixels, TILE_PX, ITEM_THICKNESS);

  /*
   * Moved so that the grip, not the middle, sits at the origin.
   *
   * Whatever holds this puts its own origin in the fist, so shifting the
   * geometry is what makes a шабля held by the hilt rather than halfway up
   * the blade. Baked into the shared shape rather than applied by every
   * holder, so the hand and the body cannot disagree about it.
   */
  const grip = gripOf(pixels, TILE_PX, shafted(tile) ? "bottom" : "bottomLeft");
  const positions = new Float32Array(mesh.positions);
  for (let i = 0; i < positions.length; i += 3) {
    positions[i] = positions[i]! - grip.x;
    positions[i + 1] = positions[i + 1]! - grip.y;
  }

  // The tile's own corner of the sheet. The extruder works in 0..1 within a
  // tile and knows nothing about where that tile sits.
  const { u, v, s } = tileRect(tile);
  const uvs = new Float32Array(mesh.uvs.length);
  for (let i = 0; i < mesh.uvs.length; i += 2) {
    uvs[i] = u + mesh.uvs[i]! * s;
    uvs[i + 1] = v + mesh.uvs[i + 1]! * s;
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute("uv", new THREE.BufferAttribute(uvs, 2));
  geometry.setIndex(mesh.indices);
  shapes.set(tile, geometry);
  return geometry;
}

/**
 * Give back a held mesh's own geometry and material.
 *
 * Unlike a body, a held thing owns both: the geometry carries UVs cut to one
 * tile and the material is per mesh, so nothing is shared and nothing else
 * goes blank when this is dropped. The atlas texture is shared and stays.
 */
export function disposeHeldMesh(thing: THREE.Object3D): void {
  // A model is shared whole - geometry, materials and textures belong to
  // the one loaded copy - and a clone of it owns nothing to give back.
  if (!(thing instanceof THREE.Mesh)) return;
  // A block's cube is its own; a drawn item's solid form is shared by every
  // copy of that item and is kept.
  if (thing.userData.sharedGeometry !== true) thing.geometry.dispose();
  const material = thing.material;
  if (Array.isArray(material)) for (const m of material) m.dispose();
  else material.dispose();
}
